import {
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import { countries } from '@shinetools/geo-library';
import { getColorValue } from '@shinetools/sunshine';
import { Typography } from '@shinetools/sunshine-universal';

import { InternationalTransferCurrency } from '__generated__/GQL';
import SunshineIcon from 'components/_core/SunshineIcon';

const OPTION_BACKGROUND_COLOR = getColorValue('grey.200');
const OPTION_TEXT_COLOR = getColorValue('grey.800');

type OptionCurrency = Pick<
  InternationalTransferCurrency,
  'code' | 'name' | 'isoCountryCode' | 'supportsDecimals'
>;

const CurrencyInput = ({
  containerWidth = 200,
  currencies,
  onCurrencyChange,
  selectedCurrency,
}: {
  onCurrencyChange: (option: OptionCurrency) => void;
  currencies: OptionCurrency[];
  containerWidth?: number;
  selectedCurrency: OptionCurrency;
}) => {
  const handleCurrencyChange = (selectedOption: OptionCurrency) => {
    onCurrencyChange(selectedOption);
  };

  if (currencies.length === 0) {
    return null;
  }

  return (
    <Menu autoSelect={false}>
      {({ isOpen }) => (
        <>
          <MenuButton
            _hover={{
              backgroundColor: 'grey.300',
            }}
            as={Button}
            backgroundColor="grey.200"
            borderRadius="radius-8"
            gap={8}
            height={28}
            padding="2px 4px 2px 8px"
            rightIcon={
              <SunshineIcon
                color="grey.800"
                name={isOpen ? 'chevron-up' : 'chevron-down'}
                size="icon-24"
              />
            }
            sx={{ '.chakra-button__icon': { margin: 0 } }}
          >
            <Typography.Text bold>{selectedCurrency?.code}</Typography.Text>
          </MenuButton>
          <MenuList
            border="transparent"
            boxShadow="0px 4px 16px 0px rgba(239, 235, 235, 0.45), 0px 4px 8px 0px rgba(239, 235, 235, 0.45)"
            boxSizing="border-box"
            data-focus-visible-disabled={true}
            padding={0}
            position="absolute"
            right={-6}
            top={4}
            width={containerWidth}
          >
            {currencies.map((currency) => {
              const isSelected = currency.code === selectedCurrency.code;
              const selectedCurrencyFlag =
                countries[currency.isoCountryCode as keyof typeof countries]
                  ?.emoji || '';
              return (
                <MenuItem
                  _after={{
                    alignSelf: 'stretch',
                    borderBottom: '1px solid var(--colors-grey-300)',
                    content: '""',
                    display: 'flex',
                  }}
                  _first={{
                    borderTopRadius: 4,
                  }}
                  _hover={{
                    bg: OPTION_BACKGROUND_COLOR,
                    color: OPTION_TEXT_COLOR,
                  }}
                  _last={{
                    _after: {
                      display: 'none',
                    },
                    borderBottomRadius: 4,
                  }}
                  alignItems="start"
                  backgroundColor={
                    isSelected ? OPTION_BACKGROUND_COLOR : 'white'
                  }
                  flexDirection="column"
                  key={currency.code}
                  onClick={() => handleCurrencyChange(currency)}
                  padding={0}
                >
                  <div className="custom-option" style={{ padding: 10 }}>
                    <HStack gap={6}>
                      <span aria-label="flag" role="img">
                        {selectedCurrencyFlag}
                      </span>
                      <Typography.Text> </Typography.Text>
                      <Typography.Text bold>{currency.code}</Typography.Text>
                      <Typography.Text bold>-</Typography.Text>
                      <Typography.Text variant="secondary">
                        {currency.name}
                      </Typography.Text>
                    </HStack>
                  </div>
                </MenuItem>
              );
            })}
          </MenuList>
        </>
      )}
    </Menu>
  );
};

export default CurrencyInput;
